import {
  getLeaveBalancesOfUser,
  updateLeaveBalancesTotalDays,
} from "@/api/leave-balances";
import EditButton from "@/components/buttons/EditButton";
import Card from "@/components/Card";
import NumberInput from "@/components/form/NumberInput";
import { useCurrentUser } from "@/context/UserContext";
import { toast } from "@/hooks/use-toast";
import { LeaveBalanceDTO } from "@/models/leave-balances.model";
import { RoleType } from "@/models/user.model";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

// only admin can edit this
interface Props {
  userId: number;
  leaveBalances: LeaveBalanceDTO[];
  setLeaveBalances: Dispatch<SetStateAction<LeaveBalanceDTO[]>>;
}
function LeaveAllowanceTab({ userId, leaveBalances, setLeaveBalances }: Props) {
  const { currentUser } = useCurrentUser();
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [totalDaysValues, setTotalDaysValues] = useState<
    Record<number, number>
  >({});

  // useEffect(() => {
  //   const fetchLeaveBalance = async () => {
  //     console.log("fetching leave balance for user", userId);
  //     if (!userId) return;
  //     const res = await getLeaveBalancesOfUser(userId);
  //     setLeaveBalances(res);
  //   };
  //   fetchLeaveBalance();
  // }, [userId]);

  useEffect(() => {
    if (leaveBalances?.length > 0) {
      const initialValues = leaveBalances.reduce(
        (acc, balance) => ({
          ...acc,
          [balance.leaveType.id]: balance.totalDays,
        }),
        {}
      );
      setTotalDaysValues(initialValues);
    }
  }, [leaveBalances]);
  const createSetValueHandler =
    (id: number): Dispatch<SetStateAction<number>> =>
      (value) => {
        setTotalDaysValues((prev) => ({
          ...prev,
          [id]: typeof value === "function" ? value(prev[id]) : value,
        }));
      };

  const handleCancel = () => {
    if (!leaveBalances) return;
    const initialValues = leaveBalances.reduce(
      (acc, balance) => ({
        ...acc,
        [balance.leaveType.id]: balance.totalDays,
      }),
      {}
    );
    setTotalDaysValues(initialValues);
    setIsReadOnly(true);
  };

  const isNoChange = () => {
    return (
      leaveBalances?.every(
        (balance) => totalDaysValues[balance.leaveType.id] === balance.totalDays
      ) || !leaveBalances
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    console.log("Submitting leave balances");
    e.preventDefault();
    try {
      const res = await updateLeaveBalancesTotalDays(userId, totalDaysValues);
      if (res) {
        setLeaveBalances(res);
        setIsReadOnly(true);

        toast({
          title: "Leave Allowance Updated!",
          description: "Leave allowance has been updated.",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to update leave allowance.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("error", error);
      toast({
        variant: "destructive",
        title: error.response.data.message || "An error occurred",
        description: error.response.data.error || "Please try again",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col flex-1 p-5 min-h-0 w-full">
        <div className="flex justify-between">
          <div>
            <div className="font-bold text-lg">Leave Allowance</div>
            <div className="w-full flex flex-col">
              <div
                className={` text-gray-500 text-sm transition-all duration-200 h-12`}
              >
                {isReadOnly
                  ? "The number of days of leave employee is entitled to this year."
                  : "Edit information below."}
              </div>
            </div>
          </div>
          {currentUser?.role === "ADMIN" && (
            <EditButton
              isEditing={isReadOnly}
              handleEditing={() => setIsReadOnly(false)}
              handleCancel={handleCancel}
              isNoChange={isNoChange}
              handleSave={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>

        <div className="h-full overflow-y-auto flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-6 mt-3 h-max">
            {/* if no leave balances, show nothing */}
            {leaveBalances.filter(x => x.leaveType.name !== "Unpaid Leave").length === 0 ? (
              <div className="text-gray-500 italic text-sm">No leave allowance found for user.</div>
            ) : (
              leaveBalances.filter(x => x.leaveType.name !== "Unpaid Leave").map((leaveBalance) => (
                <div key={leaveBalance.id}>
                  <div className="flex gap-2 flex-col">
                    <div>
                      <NumberInput
                        label={leaveBalance.leaveType.name}
                        value={totalDaysValues[leaveBalance.leaveType.id]}
                        setValue={createSetValueHandler(
                          leaveBalance.leaveType.id
                        )}
                        readOnly={isReadOnly}
                      />
                      {leaveBalance.leaveType.name === "UNPAID" &&
                        currentUser?.role === RoleType.ADMIN && (
                          <div className="text-sm text-gray-500 mt-2">
                            For unpaid leave, leave empty if there is no limit
                            enforced.
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LeaveAllowanceTab;
